import {
  DEFAULT_TIMEZONE, timezoneOptions, zoneOffsetLabel
} from '../../organigram/org-location-modal/org-location-form';
// The shape a trigger is EDITED in, and the round-trip between that and the node's `data`.
//
// Import-free of Angular and services on purpose (same rule as org-node-editor.model.ts): the spec
// loads this directly and must not drag in OrganigramApiService with its DI / Firebase chain. The
// schedule and payload helpers it builds on are pure for the same reason, so composing them costs
// nothing.
//
// Why a draft at all, rather than editing `data` in place: the wizard's picker state
// (`schedule_spec`) is what a form needs to reopen on - a cron string cannot be reversed back into
// "every second Tuesday" without guessing - so the editable shape and the stored shape genuinely
// differ, and the two conversions below are where that difference is admitted.

import {
  ScheduleSpec, blankSchedule, buildCron, buildRunAt, isOneTime, scheduleError, describeSchedule,
  parseCron
} from '../org-trigger-modal/org-trigger-schedule';
import { parseExampleData, payloadFieldNames } from '../org-trigger-modal/org-trigger-template';
import {
  sampleForCollection, expandTokens, collapseTokens, changeableFieldsFor, fieldLabel
} from '../org-trigger-modal/org-trigger-collections';
// The `when.if` comparison is JSON-logic, the same dialect a workflow ARROW's condition uses, so the
// build/parse/validate/preview helpers are shared rather than written twice. That module has no
// imports of its own, so pulling it in here keeps this one Angular-free and service-free.
import {
  conditionValidationError, rulePreview, buildIfRule, buildAllRule, parseAllRule,
  WORKFLOW_OPERATORS, WorkflowOperator, EdgeComparison
} from '../../workflow-graph/logic/workflow-condition';

// Master switch for every trigger AFFORDANCE in the organigram: the Add menu's "Trigger" entry and
// the Edit button on a trigger node, in BOTH the overview tree and the canvas. It was off for one
// staging deploy while trigger editing was finished; set back to true once that shipped.
//
// Deliberately a flag rather than commented-out markup: the two views expose triggers from four
// separate places, and commenting each out invites one being missed or revived on its own. It gates
// only what the user can REACH - the editor component, its model and the save paths stay compiled
// and tested, so flipping this is the whole change.
export const TRIGGERS_ENABLED = true;

// What each kind is called on screen. The stored value ('cron') is not a word anyone should be
// shown, and the same map serves the editor's chips and the read-only card so the two cannot
// disagree about what a trigger is.
// The three kinds the backend defines. `event` and `manual` are gone: neither appears in the schema,
// and offering a kind nothing can execute is worse than a shorter list.
export const TRIGGER_KIND_LABELS: Record<string, string> = {
  webhook: 'Webhook',
  cron: 'Schedule',
  document: 'Document change'
};

// The kinds a trigger can BE, in the order both screens offer them. The map above stays the one
// place a stored value becomes words; this is that map plus an icon and an order.
//
// Both the create wizard and the editor used to hard-code their own copy of this, and the two had
// drifted: each offered 'event' and 'manual' - kinds the backend does not define, which save with
// every config group null and therefore never fire - while neither offered 'document', whose editor
// block and validation already existed. Sharing one array is what stops a kind existing on one
// screen and not the other.
export const TRIGGER_KINDS: { value: string; label: string; icon: string }[] = [
  { value: 'webhook', label: TRIGGER_KIND_LABELS['webhook'], icon: 'link-outline' },
  { value: 'cron', label: TRIGGER_KIND_LABELS['cron'], icon: 'time-outline' },
  { value: 'document', label: TRIGGER_KIND_LABELS['document'], icon: 'document-text-outline' }
];

// The zone a schedule is read in. DEFAULT_TIMEZONE, timezoneOptions and zoneOffsetLabel all come
// from org-location-form, which is where the app already solved this - the create wizard uses the
// same three. Re-exported here so this module stays the one place a trigger's shapes are defined,
// but the implementation is not duplicated: an editor offering a different list than the wizard that
// made the trigger is how a stored zone ends up unpickable.
export { DEFAULT_TIMEZONE, timezoneOptions, zoneOffsetLabel };

// What the WRITE side of a trigger looks like while it is being edited. Deliberately flat even
// though `data` nests the per-kind groups: a form binds to fields, and re-deriving which group a
// control belongs to at every [(ngModel)] would put the schema's shape into the template.
// triggerDataPatch is the single place that shape is applied.
export interface TriggerDraft {
  name: string;
  triggerType: string;
  // cron
  timezone: string;
  schedule: ScheduleSpec;
  // The raw expression, kept ONLY when it is one the picker cannot represent (a hand-written cron).
  // Empty otherwise, and the picker is the truth.
  rawCron: string;
  // Whether agents in this trigger's runs may act without a human confirming each action. Shallow
  // on `data`, not inside a per-kind group: it applies to every kind, because every kind can start
  // an unattended run.
  allowSensitiveActions: boolean;
  // webhook
  requireSignature: boolean;
  requireIdempotencyKey: boolean;
  // document
  watchCollection: string;
  on: string[];
  // The optional COMPARISON inside `when`, as the stored JSON-logic rule object - not a parsed
  // field/operator/value triple. Null when there is none, which is every trigger the wizard makes.
  //
  // The OBJECT, for two reasons, both about not destroying what is already stored:
  //   - a hand-written `and`/`or` is a rule the three-control form cannot render, and keeping the
  //     object verbatim is what lets it ride through a save untouched instead of being replaced by
  //     whatever the empty form builds - the same bargain `rawCron` strikes for a hand-written cron;
  //   - a parse -> build round trip is deliberately LOSSY (parseIfRule folds `===` onto `==`,
  //     buildIfRule coerces "08001" to the number 8001), so carrying the triple would silently
  //     rewrite somebody's rule the first time an unrelated field on the trigger was saved.
  // The three controls are the COMPONENT's own state, seeded from this and written back only on a
  // real edit.
  //
  // The `{var}` path inside is ROOTED at a snapshot ('after.status'); `when.changed` beside it is
  // NOT - it names a field inside the document ('status'). The two look alike and mean different
  // things; conditionFieldsFor is the one place that root is added.
  whenIf: any;
  // The fields that have to have changed, as `when.changed` stores them: DOCUMENT-RELATIVE
  // ('status'), never rooted.
  //
  // TYPED, like whenIf. This used to be derived by scanning every instruction for {{after.x}}
  // placeholders, which made the message do two jobs at once: a sentence mentioning a field silently
  // became a filter on that field, ANDed with the comparison the user could actually see. A trigger
  // whose instruction said "Neue Bewerbung von {{after.vorname}}" and whose condition said
  // "after.status is approved" fired only when the NAME changed and the status was approved - so
  // approving an application did nothing, the screen said only "status is approved", and there was
  // nothing to look at to find out why. It also moved on its own: the derivation ran from
  // messagePatch too, so editing an instruction on the canvas or deleting a step re-scoped the
  // trigger with no editor open.
  //
  // Now it is one more line in the same "Only when" list the comparison lives in, written with the
  // `has changed` check, and the instruction is only the text the agent receives.
  whenChanged: string[];
  // What to say to each NODE under this trigger, keyed by node id. This is the whole message
  // configuration - there is no trigger-level message any more.
  //
  // The trigger IS the workflow: its children are the nodes, and each carries its own instruction,
  // the way a node in a flow builder carries its own config. It is stored here rather than on the
  // node because a node is an agent or a person - shared, and reachable from more than one trigger,
  // so two workflows pointing at the same agent would otherwise fight over one prompt. A trigger fans out to its children, and each one
  // wants its own instruction - the same idea as a node carrying its own config in n8n. It lives on
  // the trigger rather than on the agent because an agent is a shared catalogue entity: two triggers
  // pointing at the same agent need different prompts.
  //
  // FLAT here (nodeId -> prompt) though it is stored as `{ nodeId: { message } }`: a form binds to a
  // value, and making every [(ngModel)] reach through a wrapper puts the schema's shape into the
  // template - the same reason the per-kind groups are flattened onto this draft.
  messages: Record<string, string>;
  // Which snapshot the message is written against. A change carries BOTH - the document as it was
  // and as it now is - so this does not choose what the backend sends, it chooses which half the
  // placeholders point at: {{after.email}} against the new value, {{before.email}} against
  // the old one. 'after' by default, which is what "something happened, go look at it" means; the
  // 'before' half matters for a delete, where the new state is nothing.
  sendSnapshot: string;
}

// The document-trigger events, and the default when none is chosen.
export const DOCUMENT_EVENTS = ['create', 'update', 'delete'];

// The two halves of a document change, and the placeholder root each one produces.
export const DOCUMENT_SNAPSHOTS: { value: string; label: string }[] = [
  { value: 'after', label: 'After the change' },
  { value: 'before', label: 'Before the change' }
];

// One half of the change, as a group of fields the comparison can read from.
export interface ConditionFieldGroup {
  label: string;
  fields: { value: string; label: string }[];
}


// --- The "Only when" list ----------------------------------------------------------------------
//
// One list on screen, two halves in storage. Every line reads `<field> <check> <value?>`, and where
// it lands is an implementation detail the person writing it never sees:
//
//   `after.status has changed`     -> when.changed: ['status']   (the root is stripped)
//   `after.status is approved`     -> when.if: {'==': [{var:'after.status'}, 'approved']}
//
// Both halves narrow, so the list reads as AND - which is what the two halves already meant when one
// of them was derived behind the user's back.

// The check that asks whether a field changed at all. NOT in WORKFLOW_OPERATORS, which arrows share:
// an arrow gates on one step's output and has no before/after halves to compare, so offering it
// there would be a control that cannot mean anything.
export const CHANGED_OPERATOR = 'changed';

// The checks a document trigger offers. `has changed` first: it is the one most document triggers
// want, and it is the only one the old derived filter could express.
export const TRIGGER_CONDITION_OPERATORS: WorkflowOperator[] = [
  { value: CHANGED_OPERATOR, label: 'has changed', short: 'changed', takesValue: false },
  ...WORKFLOW_OPERATORS
];

// Whether the chosen check needs a value box. Its own reader rather than workflow-condition's,
// because that one defaults an unknown operator to TAKING a value - and `changed` is unknown there.
export function triggerOperatorTakesValue(operator: string): boolean {
  return TRIGGER_CONDITION_OPERATORS.find(o => o.value === operator)?.takesValue !== false;
}

// One line of the list, exactly as the three controls hold it.
export interface ConditionRow {
  field: string;
  operator: string;
  value: string;
}

// The roots a field path may carry, longest-first so 'before.' is not half-stripped by 'be'.
const SNAPSHOT_ROOTS = ['before.', 'after.', 'data.'];

// A rooted path -> the document-relative field `when.changed` names. A path THROUGH an array is
// truncated at the array: 'Ergebnis[].Job' is not addressable as a field, 'Ergebnis' is, and "did
// that array change" is the closest true statement. Bare paths pass through, so a hand-typed
// 'status' works as well as a picked 'after.status'.
export function documentFieldPath(field: string): string {
  let path = (field || '').trim();
  for (const root of SNAPSHOT_ROOTS) {
    if (path.startsWith(root)) { path = path.slice(root.length); break; }
  }
  const cut = path.indexOf('[');
  return (cut >= 0 ? path.slice(0, cut) : path).replace(/\.$/, '');
}

// The list -> the two halves as they are stored.
//
// A `has changed` row contributes its field to `changed`, de-duplicated and in the order written; a
// row with no field at all contributes nothing, so a half-typed line cannot store `{var: ''}` - a
// rule that is false for every document, reads back as configured, and never fires.
export function conditionRowsToWhen(rows: ConditionRow[]): { changed: string[]; rule: any } {
  const changed: string[] = [];
  const rules: any[] = [];
  for (const row of rows || []) {
    const field = (row?.field || '').trim();
    if (!field) { continue; }
    if (row.operator === CHANGED_OPERATOR) {
      const path = documentFieldPath(field);
      if (path && changed.indexOf(path) < 0) { changed.push(path); }
      continue;
    }
    rules.push(buildIfRule(field, row.operator, row.value));
  }
  return { changed, rule: buildAllRule(rules) };
}

// The stored halves -> the list, or null when the rule is a shape the form cannot render and must
// keep verbatim (a hand-written `or`, a nested `and`). The changed half is always readable, so it is
// only ever the rule that refuses.
//
// A changed field is re-rooted at `after.` for display. The root is cosmetic for this check - "did
// the status change" reads both halves by definition - and `when.changed` does not store one, so
// there is nothing to preserve and one consistent root beats guessing.
export function conditionRowsFromWhen(changed: string[], rule: any): ConditionRow[] | null {
  const comparisons = parseAllRule(rule);
  if (comparisons === null) { return null; }
  const rows: ConditionRow[] = (changed || [])
    .filter(f => (f || '').trim())
    .map(f => ({ field: `after.${(f || '').trim()}`, operator: CHANGED_OPERATOR, value: '' }));
  for (const c of comparisons as EdgeComparison[]) {
    rows.push({ field: c.field, operator: c.operator, value: c.value });
  }
  return rows;
}

// The fields a document trigger's `when.if` can compare, ROOTED at the half the rule reads from.
//
// changeableFieldsFor returns DOCUMENT-RELATIVE paths ('status', 'Wohnort.city') because that is
// `when.changed`'s vocabulary - it names a field inside the document. A rule's `{var}` is resolved
// against the template context instead, where `before` and `after` are siblings holding the
// document, so the same field is 'after.status' there. Handing the bare path to buildIfRule stores
// `{var: 'status'}`, which resolves against nothing: it saves cleanly, reads back as configured and
// never fires. This is the ONE place that root is added.
//
// BOTH halves are offered, deliberately independent of `sendSnapshot`. That chip picks which half
// the MESSAGE is written against and is a display preference the backend never reads; a gate
// legitimately asks about either half - "the status is now approved" is after.status, "it was not
// approved before" is before.status. Re-rooting a stored rule when the chip moves would change what
// the trigger asks rather than how it reads.
//
// Empty for a collection whose shape is not confirmed, so the form can say so rather than render an
// empty picker.
export function conditionFieldsFor(collection: string): ConditionFieldGroup[] {
  const paths = changeableFieldsFor(collection);
  if (!paths.length) { return []; }
  return DOCUMENT_SNAPSHOTS.map(s => ({
    label: s.label,
    fields: paths.map(p => ({ value: `${s.value}.${p}`, label: fieldLabel(collection, p) }))
  }));
}

// Why a comparison would never match, or '' when it can. Two causes, both of which leave a trigger
// inert while it looks perfectly configured - the silent failure this whole area exists to prevent.
//
//   - The path is not ROOTED at a half of the change. `before` and `after` are siblings in the
//     template context, so a bare `status` addresses nothing. Reachable because the field can be
//     TYPED, which is the whole point of the box: a picked field is always rooted, a typed one is
//     only rooted if the person knew to.
//   - The path reads a half the watched events cannot produce: a created document has no BEFORE and
//     a deleted one has no AFTER.
//
// A WARNING rather than a validation error, for both. Rooting is something you pass through while
// typing ("a", "af", "aft"...), and widening the events is a fix the person may want to make after
// writing the rule - refusing the save would block the order most people work in. Validation stays
// for what is unambiguously incomplete: a comparison with no value.
// The roots a rule's `{var}` may legitimately carry. `data` is an alias for `after` - the two point
// at the same document (see placeholderRootFor) - so a hand-written rule using it is correct and must
// not be warned about.
const CONDITION_ROOTS = ['after.', 'before.', 'data.'];

// The three checks for ONE line of the "Only when" list.
//
// A `has changed` line is exempt from the rooting check, and only from that one: its field is stored
// document-relative, so a bare `status` is the CORRECT thing to type there and warning about it would
// be telling the truth about the wrong control. The event checks still apply - asking whether a field
// changed is meaningless when only creations are watched, for the same reason reading `before` is.
export function conditionRowWarning(row: ConditionRow, on: string[]): string {
  const field = (row?.field || '').trim();
  if (!field) { return ''; }
  if (row.operator === CHANGED_OPERATOR) {
    const events = on || [];
    if (events.length && !events.some(e => e === 'update')) {
      return 'This asks whether a field changed, but no update is watched - a document that is only '
        + 'created or deleted has no earlier value to compare against.';
    }
    return '';
  }
  return fieldWarning(field, on);
}

function fieldWarning(raw: string, on: string[]): string {
  const field = (raw || '').trim();
  if (!field) { return ''; }
  const events = on || [];
  // FIRST, because it is the certain one: the others describe a gate that cannot match given the
  // events, this describes one that cannot match at all. `before` and `after` are siblings in the
  // template context, so a bare `status` addresses nothing and is false for every document.
  if (!CONDITION_ROOTS.some(r => field.startsWith(r))) {
    return `"${field}" is not rooted at a half of the change, so it matches nothing. `
      + 'Start it with after. or before.';
  }
  if (field.startsWith('before.') && events.length && !events.some(e => e !== 'create')) {
    return 'This check reads the document as it was before the change, but only new documents are '
      + 'watched - there is no earlier version to compare.';
  }
  if (field.startsWith('after.') && events.length && !events.some(e => e !== 'delete')) {
    return 'This check reads the document as it is after the change, but only deletions are '
      + 'watched - there is nothing left to compare.';
  }
  return '';
}

export function blankTriggerDraft(): TriggerDraft {
  return {
    name: '', triggerType: 'webhook', timezone: DEFAULT_TIMEZONE,
    schedule: blankSchedule(), rawCron: '',
    allowSensitiveActions: false, requireSignature: true, requireIdempotencyKey: false,
    watchCollection: '', on: [...DOCUMENT_EVENTS], sendSnapshot: 'after', whenIf: null, whenChanged: [],
    messages: {}
  };
}

// The root a document trigger's placeholders hang off.
//
// `data`, `before` and `after` are SIBLINGS in the template context, not a tree - and `data` IS
// `after`, the two point at the same document. So the root is the sibling itself: `after.email`,
// never `data.after.email`, which asks for a field called `after` inside the document and renders
// empty. That mistake is why a trigger could look correctly configured and still send nothing.
//
// And the field sits at the path the DOCUMENT gives it: an applicant's address is `after.email`,
// not `after.data.email` - `email` is top-level on that collection. Both halves of this were
// confirmed against a live trigger (Christian, 2026-08-11); neither is inferred.
//
// `after` rather than its alias `data`, because it names which half you are reading and reads as the
// opposite of `before`.
export function placeholderRootFor(draft: TriggerDraft | null | undefined): string {
  if (draft?.triggerType !== 'document') { return 'data'; }
  return draft?.sendSnapshot === 'before' ? 'before' : 'after';
}

// The object a trigger's placeholders are written against. A webhook has a real received request -
// the backend writes it on a test hit. A document trigger has no test hit, so the example pasted
// when it was created (`defaultData`) is the only shape available, and it is the one the pills are
// derived from.
export function triggerSampleFrom(data: Record<string, any> | null | undefined): any {
  if (data?.['trigger_type'] === 'document') {
    // The collection's own shape is the fallback, and for a trigger made in the wizard it is the
    // ONLY source: that screen no longer asks for an example, because the chips are built from the
    // collection you picked. Without this the editor would open a document trigger with no chips at
    // all - the create screen would offer field names the edit screen could not.
    return data?.['defaultData'] ?? sampleForCollection(group(data, 'document')['watch_collection']);
  }
  return readSample(data).sample;
}

// Only the scheduled kind has a schedule. Kept as a function rather than a boolean on the draft so
// there is one answer to "is this scheduled" and it cannot go stale against triggerType.
export function isScheduledDraft(draft: TriggerDraft): boolean {
  return draft?.triggerType === 'cron';
}

// A node's stored data -> the editable draft.
//
// The schedule is PARSED back out of `cron`/`run_at`, because the schema stores those and nothing
// else - there is no schedule_spec to restore from any more. An expression the picker cannot
// represent (a hand-written one) is kept verbatim in `rawCron` and left alone rather than shown as
// the nearest frequency, which would overwrite a real schedule on the next save.
export function triggerDraftFromNode(
  node: { name?: string; data?: Record<string, any> } | null | undefined
): TriggerDraft {
  const data = node?.data || {};
  const hook = group(data, 'webhook');
  const clock = group(data, 'cron');
  const doc = group(data, 'document');
  // `cron` is a GROUP, not a string: the expression sits at data.cron.expression beside the zone and
  // the one-time date. Read only from the group - both writers (this module and the create wizard)
  // emit the grouped shape, so a flat key means a node that predates it and there are none.
  const expression = clock['expression'] || '';
  const parsed = parseCron(expression, clock['run_at']);
  return {
    name: node?.name || '',
    triggerType: data['trigger_type'] || 'webhook',
    messages: readMessages(data, group(data, 'document')['watch_collection']),
    timezone: clock['timezone'] || DEFAULT_TIMEZONE,
    schedule: parsed || blankSchedule(),
    rawCron: parsed ? '' : expression,
    // Defaults matter here: an omitted toggle means the backend's default, and require_signature
    // defaults to TRUE. Reading a missing key as false would quietly turn signing off on save.
    // Read explicitly, because the patch now WRITES it: a draft that defaulted to false would turn
    // it off the first time an unrelated field on a permitted trigger was edited.
    allowSensitiveActions: data['allow_sensitive_actions'] === true,
    requireSignature: hook['require_signature'] !== false,
    requireIdempotencyKey: hook['require_idempotency_key'] === true,
    watchCollection: doc['watch_collection'] || '',
    on: Array.isArray(doc['on']) && doc['on'].length ? [...doc['on']] : [...DOCUMENT_EVENTS],
    // 'after' unless stored otherwise: a trigger written before this field existed templated against
    // the new state, which is what 'after' means.
    sendSnapshot: doc['send'] === 'before' ? 'before' : 'after',
    // The comparison verbatim - see the field's own note for why the rule object and not a triple.
    whenIf: storedIf(doc),
    whenChanged: storedChanged(doc)
  };
}

// One per-type config group, tolerating its absence or a value that is not an object. `data.cron`
// in particular used to be a STRING, so a node written before the regroup would otherwise spread a
// string across the draft.
// Keys no longer part of the schema: the message's old name, and the flat spellings of what now
// lives inside the `cron` and `document` groups. Nothing writes them any more.
//
// They are DROPPED from the patch, not nulled. A PATCH replaces `data` wholesale, so a key left out
// of the object is genuinely deleted - whereas nulling it leaves `run_at: null` on the document
// forever. That is what the earlier version did, and it is why triggers written before this still
// carry a row of nulls. Omitting them means the next save of such a trigger cleans it up, and a
// trigger that never had them is untouched either way.
// `payload` is here now: it was the single trigger-level message, replaced by one message per node
// in `messages`. Left behind it would be a second, stale copy of an instruction that has moved.
const DEAD_KEYS = ['user_message', 'payload', 'run_at', 'timezone', 'watch_collection', 'on', 'when', 'schedule_spec'];

function withoutDeadKeys(data: Record<string, any> | null | undefined): Record<string, any> {
  const out: Record<string, any> = {};
  for (const key of Object.keys(data || {})) {
    if (!DEAD_KEYS.includes(key)) { out[key] = (data as Record<string, any>)[key]; }
  }
  return out;
}

function group(data: Record<string, any> | null | undefined, name: string): Record<string, any> {
  const value = data?.[name];
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

// Kept as a named helper because readSample and webhookFromData both want exactly this one.
function webhookGroup(data: Record<string, any> | null | undefined): Record<string, any> {
  return group(data, 'webhook');
}

// The comparison stored inside a document trigger's `when`, or null. Never reached into blind: the
// create wizard writes `when: null` on every document trigger it makes, and a trigger older than the
// group could carry something that is not an object at all.
function storedIf(doc: Record<string, any>): any {
  const when = doc?.['when'];
  return when && typeof when === 'object' && !Array.isArray(when) ? (when['if'] ?? null) : null;
}

// The changed-field list stored inside `when`, or []. Same blind-read guard as storedIf, plus the
// element filter: a list is only useful if its entries are non-empty strings, and one that is not an
// array at all reads as no filter rather than throwing the editor down on open.
export function storedChanged(doc: Record<string, any>): string[] {
  const when = doc?.['when'];
  if (!when || typeof when !== 'object' || Array.isArray(when)) { return []; }
  const changed = when['changed'];
  if (!Array.isArray(changed)) { return []; }
  return changed.filter(f => typeof f === 'string' && f.trim()).map(f => f.trim());
}

// `when` as it is STORED: the fields that must have changed, and the comparison, in one object.
//
// BOTH halves are now typed - two lines of the same "Only when" list, told apart only by the check
// each one uses. `changed` used to be derived from the instructions instead, which meant a sentence
// mentioning a field silently narrowed the trigger to that field; see TriggerDraft.whenChanged.
//
// Null when there is NEITHER: the key exists to narrow, and an empty narrowing is no narrowing. Each
// half is OMITTED rather than nulled when absent, so `when` never carries a key that says nothing -
// the same rule DEAD_KEYS follows one level up.
//
// The two halves keep their own vocabularies, which is why they are separate keys and not one list:
// `changed` is document-relative ('status'), the rule's `{var}` is rooted ('after.status'). The list
// on screen hides that split - conditionRowsToWhen is the one place it is applied.
function writeWhen(changed: string[], rule: any): Record<string, any> | null {
  const fields = (changed || []).filter(f => (f || '').trim());
  const gated = rule !== null && rule !== undefined;
  if (!fields.length && !gated) { return null; }
  return { ...(fields.length ? { changed: fields } : {}), ...(gated ? { if: rule } : {}) };
}

// An instruction in the NORMALISED shape the app works with, whatever the document stored. It is
// STORED as a map keyed by the recipient node's id (`messages[nodeId] = { message }`): an instruction
// is bound to exactly one recipient, so the node id IS its identity and there is no separate record.
//
// `id` is a canvas handle, not a stored field - the graph lays a message out as its own item keyed by
// id, and a message must never share an id with the node it feeds. So it is `msg:<nodeId>` for a
// map-stored instruction (see readMessageList), distinct from the node's own id.
//
// `node_id` is the recipient. It is only ever '' for a malformed legacy record; the map shape cannot
// express an unattached instruction, which is the point - every instruction has a recipient.
export interface TriggerMessage {
  id: string;
  node_id: string;
  message: string;
}

// Reads whichever shape is on the document into the normalised record list.
//
// The MAP keyed by node id is the shape written now; its record id is `msg:<nodeId>`, distinct from
// the node's own id so a message and the node it feeds never collide as canvas items. That id is
// unique only WITHIN one trigger's map - the canvas aggregates every trigger's messages, so
// loadWorkflowMessages qualifies it by the owning trigger before using it as a canvas item id.
//
// The LIST of `{ id, node_id, message }` is the earlier shape and is still read, so a trigger written
// before the change keeps its instructions - the first save rewrites it as a map. Transitional:
// remove the array branch once no trigger carries the list.
export function readMessageList(data: Record<string, any> | null | undefined): TriggerMessage[] {
  const raw = data?.['messages'];
  if (Array.isArray(raw)) {
    return raw
      .filter(m => m && typeof m === 'object')
      .map(m => ({
        id: String(m['id'] || ''),
        node_id: String(m['node_id'] || ''),
        message: typeof m['message'] === 'string' ? m['message'] : ''
      }))
      .filter(m => m.id || m.node_id || m.message);
  }
  if (!raw || typeof raw !== 'object') { return []; }
  return Object.keys(raw).map(nodeId => {
    const entry = raw[nodeId];
    const message = entry && typeof entry === 'object' ? entry['message'] : entry;
    return { id: 'msg:' + nodeId, node_id: nodeId, message: typeof message === 'string' ? message : '' };
  });
}

// ONE node's instruction, read back out of the trigger for display.
//
// Collapsed on the way out, the mirror of expandTokens on the way in: the document holds
// `{{after.vorname}}` and the box shows `{{first_name}}`, so nobody has to know the field paths to
// read what a node was told to do.
//
// '' for a node with no instruction, which is the same thing the box shows for a node that has one
// and it is empty - there is no third state to distinguish.
export function messageFor(
  data: Record<string, any> | null | undefined, nodeId: string
): string {
  if (!nodeId) { return ''; }
  const found = readMessageList(data).find(m => m.node_id === nodeId);
  if (!found) { return ''; }
  return collapseTokens(found.message, group(data, 'document')['watch_collection'] || '');
}

// ONE node's instruction, merged into the trigger it belongs to.
//
// The instruction is typed on the NODE (its panel on the canvas) and stored on the TRIGGER, because
// `messages` is the trigger's field - the node itself is never written by this. So the save is a
// patch of a different document from the one being edited, and this builds it.
//
// MERGED, never rebuilt: a PATCH replaces `data` wholesale, so a body built from this one box would
// delete every other node's instruction. The existing map is read out of `existingData` and only the
// one key is touched.
//
// Blank CLEARS the key rather than storing "". An empty string is an instruction that says nothing,
// and `messages` is meant to be exactly the set of nodes with something to do.
//
// `when` is NOT touched. An instruction is what an agent is told; it has nothing to do with when the
// trigger fires, which is asked in the editor's "Only when" list and written only by its Save. This
// function used to recompute a changed-field filter from the merged messages, so every caller here
// silently re-scoped the trigger - see the note in the body.
export function messagePatch(
  existingData: Record<string, any> | null | undefined, nodeId: string, text: string
): Record<string, any> {
  const out: Record<string, any> = { ...(existingData || {}) };
  const collection = group(existingData, 'document')['watch_collection'] || '';
  // Whatever shape the document is in, out comes a node-keyed map - so a trigger written before the
  // change is converted by the first save rather than being read one way and written another.
  const map: Record<string, { message: string }> = {};
  for (const m of readMessageList(existingData)) {
    if (m.node_id) { map[m.node_id] = { message: m.message }; }
  }
  const written = (text || '').trim();
  if (written) {
    map[nodeId] = { message: expandTokens(written, collection) };
  } else {
    // Emptying the box clears the instruction, and clearing an instruction removes the key. An entry
    // saying nothing is not a state worth keeping.
    delete map[nodeId];
  }
  const keys = Object.keys(map);
  out['messages'] = keys.length ? map : null;

  // `document` is NOT touched here, deliberately. This function writes one instruction; when a
  // trigger fires is a different question, asked in the editor's "Only when" list and answered only
  // there. It used to recompute `when` from the merged messages, which meant typing on the canvas,
  // saving an unrelated step, or deleting one re-scoped the trigger - the last of those from a
  // cascade that runs with no editor open and swallows its own errors, so nothing said so.
  //
  // The spread at the top carries the group through untouched, which is all that is wanted.
  return out;
}

// The draft -> the `data` object for updateNode.
//
// THREE rules, all learned the hard way:
//
//   1. `existingData` is spread FIRST, so the identity (user_id, account_id, workflow_id) and every
//      BACKEND-WRITTEN field survives - a PATCH carrying `data` replaces the object wholesale, so
//      anything not rebuilt here is destroyed. nextRunAt, scheduleError and lastScheduledFor are
//      the backend's to write and this must never clear them.
//
//      `allow_sensitive_actions` IS written, from the draft, now that the form can set it. That
//      makes triggerDraftFromNode load-bearing: it must read the stored value rather than default
//      to false, or editing an unrelated field on a permitted trigger would silently revoke it.
//   2. Every key this function OWNS is always written, with an explicit null when it does not apply.
//      Omitting one leaves the old value behind - a trigger switched off a schedule would otherwise
//      keep firing on its stale cron.
//   3. The per-kind groups are applied HERE and nowhere else. The draft is flat so the template can
//      bind to it; this is the one place that flatness becomes the schema's shape.
export function triggerDataPatch(
  draft: TriggerDraft, existingData: Record<string, any> = {}
): Record<string, any> {
  const kind = draft?.triggerType || 'webhook';
  const scheduled = kind === 'cron';
  const once = scheduled && isOneTime(draft.schedule);
  const messages = writeMessages(draft);
  return {
    // Stripped, not spread verbatim: anything the schema has dropped is removed by being left out.
    ...withoutDeadKeys(existingData),
    trigger_type: kind,
    // Per-child prompts. Null rather than {} when there are none, so a trigger with a single
    // recipient carries no empty map - the same reason `when` is null when nothing is filtered.
    messages,
    // What an unattended run may do without a human approving each action. Never inside a group: it
    // applies to every kind.
    allow_sensitive_actions: draft?.allowSensitiveActions === true,

    // EXACTLY ONE of the three groups is populated and the other two are null. Nulled rather than
    // omitted: `data` is replaced wholesale by a PATCH, so an omitted group would leave the previous
    // kind's config in place - a trigger switched off a schedule would keep its expression, and one
    // switched off a webhook would keep a live registration under a kind that cannot use it.
    //
    // The old SHALLOW keys are nulled too, so a node written before the regroup does not end up
    // carrying both spellings and drifting between them.
    cron: scheduled ? {
      // SPREAD, not rebuilt. The scheduler's own state lives in this group - nextRunAt,
      // scheduleError, scheduleErrorAt, lastScheduledFor - and it is the backend's to write. A
      // fresh object here would delete all four on every save, which is the same wholesale-replace
      // trap as `data` itself, one level down.
      ...group(existingData, 'cron'),
      // "" rather than null for a one-time run: the empty string is what the backend reads as
      // "no recurrence", and run_at carries the date instead. Never both.
      expression: once ? '' : (draft.rawCron || buildCron(draft.schedule) || ''),
      run_at: once ? buildRunAt(draft.schedule, draft?.timezone || DEFAULT_TIMEZONE) : null,
      // REQUIRED, or the scheduler parks the trigger rather than guessing a zone.
      timezone: draft?.timezone || DEFAULT_TIMEZONE
    } : null,

    // The group is REBUILT rather than spread over, so the toggles are always written - but the ids
    // and samples the BACKEND owns inside it are carried through untouched.
    webhook: kind === 'webhook' ? {
      ...webhookGroup(existingData),
      require_signature: draft.requireSignature !== false,
      require_idempotency_key: draft.requireIdempotencyKey === true,
      // Always FALSE on save. Test mode captures a request as a sample instead of firing the
      // workflow, which is a thing you are doing right now, not a setting the trigger keeps -
      // saving is what says you are done testing, so it goes live.
      test_mode: false
    } : null,

    document: kind === 'document' ? {
      // SPREAD, for the reason the cron and webhook groups are: a PATCH replaces `data` wholesale,
      // so a key the backend owns inside this group is destroyed by a fresh object. This one was
      // rebuilt while the other two spread, which is a difference nothing intended.
      ...group(existingData, 'document'),
      watch_collection: (draft.watchCollection || '').trim(),
      on: draft.on?.length ? [...draft.on] : [...DOCUMENT_EVENTS],
      // Both halves of the "Only when" list, both typed. See writeWhen.
      when: writeWhen(draft.whenChanged, draft.whenIf),
      // Which half the message is written against. NOT read by the backend - the context always
      // carries both halves regardless - so this is a UI preference, stored only so reopening the
      // editor offers the same root it offered last time.
      send: draft.sendSnapshot === 'before' ? 'before' : 'after'
    } : null
  };
}

// The create wizard's POST-CREATE save: the message and the example as they now stand, merged over
// what the node already holds.
//
// It exists because the wizard no longer ends at Create. A webhook's address does not exist until
// its node does, so the test step - where you hit that address and read the real payload back - can
// only come after; and the whole point of getting there is to go back and write the message against
// the field names that actually arrived. That second write is this.
//
// Read-then-MERGE, never rebuilt from the form. A PATCH replaces `data` wholesale, so the
// registration, the captured sample and the identity the run is attributed to all have to come back
// out of the node first - the same trap triggerDataPatch documents, and the same one that once
// deleted user_id / account_id / workflow_id a second after the create wrote them.
//
// `defaultData` is DELETED rather than nulled when the example is cleared, for the same reason
// DEAD_KEYS are omitted: a null is a key that says nothing, sitting on the document forever.
//
// `test_mode` goes FALSE, exactly as triggerDataPatch forces it. Listening is something you are
// doing right now, and finishing is what says you are done - a trigger left in test mode captures
// requests instead of firing and looks perfectly healthy doing it. Written here rather than trusted
// to the listener's teardown, which is deliberately fire-and-forget and could land after this.
export function triggerFinishPatch(
  existingData: Record<string, any> | null | undefined,
  messages: Record<string, Record<string, any>> | null,
  example: any,
  requireSignature = true
): Record<string, any> {
  const out: Record<string, any> = { ...(existingData || {}) };
  // `messages`, not `payload`. This wrote the single trigger-level message and kept doing so after
  // the create path moved to one message per node - so a webhook finished here carried a key the
  // editor deletes as dead, and lost its instructions the first time it was opened.
  delete out['payload'];
  // Only when there ARE some. The wizard finishes a trigger that has none yet - its nodes are
  // connected afterwards - and writing null there would be this patch deleting instructions it was
  // never given, which is what `payload` used to do to messages.
  if (messages) { out['messages'] = messages; }
  if (example === null || example === undefined) {
    delete out['defaultData'];
  } else {
    out['defaultData'] = example;
  }
  // Keyed off the STORED kind rather than a flag from the form: this runs against what the backend
  // actually wrote, and a webhook group must not be conjured onto a trigger that has no webhook.
  if (out['trigger_type'] === 'webhook') {
    out['webhook'] = {
      ...group(existingData, 'webhook'),
      test_mode: false,
      // Asked at the point the secret is handed over, rather than defaulted and buried in the
      // editor: that is the moment someone decides whether they are going to sign anything.
      require_signature: requireSignature
    };
  }
  return out;
}

// The per-child prompts, with their placeholders collapsed back to tokens for display.
//
// Two shapes are read: `{ message }`, which is what is written, and a bare string, which is what the
// first version wrote. The typeof check is not data policing - the UI can only ever produce strings
// - it is there because collapseTokens calls .replace, and a stored number would throw rather than
// render oddly. Anything that is not text becomes '', and writeMessages drops empties on the way
// back out, so nothing else has to decide what a corrupt entry means.
function readMessages(data: Record<string, any> | null | undefined, collection: string): Record<string, string> {
  const out: Record<string, string> = {};
  // Keyed by NODE for the form, which binds one box per node. The stored map is already node-keyed,
  // so a save writes each box straight back under its recipient's id.
  for (const m of readMessageList(data)) {
    if (m.node_id) { out[m.node_id] = collapseTokens(m.message, collection); }
  }
  return out;
}

// The per-child prompts as STORED: a map keyed by the recipient node's id. Keyed by node because an
// instruction is bound to one recipient - the node id is its identity, so there is no record id to
// carry across a save.
//
// Empty prompts are DROPPED rather than stored blank: clearing a child's message means "use the
// trigger's own", which is the absence of a key, not an empty one. Null rather than {} when none are
// left, so a trigger with no instructions carries no empty map.
function writeMessages(draft: TriggerDraft): Record<string, { message: string }> | null {
  const source = draft?.messages || {};
  const out: Record<string, { message: string }> = {};
  for (const nodeId of Object.keys(source)) {
    const prompt = (source[nodeId] || '').trim();
    if (!prompt) { continue; }
    out[nodeId] = { message: expandTokens(prompt, draft?.watchCollection || '') };
  }
  return Object.keys(out).length ? out : null;
}

// Why the draft cannot be saved, or null when it can. One answer, so the Save button and the error
// line cannot disagree.
export function validateTriggerDraft(draft: TriggerDraft): string | null {
  if (!(draft?.name || '').trim()) { return 'Give the trigger a name.'; }
  // An instruction is NOT required to save a trigger. Instructions belong to the trigger's STEPS and
  // are written on the canvas (or the recipient boxes) AFTER the trigger and its nodes exist - so a
  // trigger with no recipients yet has no box to write in, and demanding one here was a dead end with
  // no way out. The create wizard already allows a trigger with no instruction; this matches it. A
  // trigger is valid on its name plus its per-kind config below.
  const kind = draft?.triggerType;
  if (kind === 'cron') {
    // A hand-written expression is taken as given - it came from somewhere that knew what it meant,
    // and the picker has no opinion on it.
    if (draft.rawCron) { return (draft.timezone || '').trim() ? null : 'Choose a timezone.'; }
    const problem = scheduleError(draft.schedule);
    if (problem) { return problem; }
    // The backend parks a schedule with no zone rather than guessing one, so it is required here.
    if (!(draft.timezone || '').trim()) { return 'Choose a timezone.'; }
  }
  if (kind === 'document') {
    if (!(draft.watchCollection || '').trim()) { return 'Name the collection to watch.'; }
    if (!draft.on?.length) { return 'Pick at least one change to watch for.'; }
    // A comparison the form STARTED but did not finish is refused rather than stored half-written:
    // `after.status == ""` saves cleanly, reads as configured and never matches.
    //
    // Guarded on a field having been chosen, which keeps two things true: no comparison at all is
    // the ORDINARY state and must never block a save, and a rule this form cannot render - a
    // hand-written and/or - parses to null and is taken as given, the same bargain a hand-written
    // cron gets above. Do not drop that guard to "validate everything": such a rule was not built
    // here and is not being rebuilt, so there is nothing in it that could be half-written.
    //
    // EVERY line of the list, not the first: they are ANDed, so one half-written line makes the whole
    // trigger inert, and a list is exactly where a line scrolls out of sight and gets forgotten.
    const cmps = parseAllRule(draft.whenIf);
    for (const cmp of cmps || []) {
      // Its OWN message for a rule with no field, rather than conditionValidationError's: that one
      // reads "for example output.category", which is the arrow editor's vocabulary and names a path
      // no trigger screen has ever shown. Reachable only from a hand-written rule now that the form
      // trims before it builds, but a `{var: ''}` gate is false for every document, so it has to be
      // refused rather than stored looking configured.
      if (!cmp.field.trim()) { return 'Choose a field for the condition to check.'; }
      const problem = conditionValidationError(cmp.field, cmp.operator, cmp.value);
      if (problem) { return problem; }
    }
  }
  return null;
}

export interface TriggerFact {
  label: string;
  value: string;
}

// What to SHOW for a trigger in a read-only detail card.
//
// The generic node card is useless here: it prints Type / Status / Description / Goals and an
// allow-listed slice of `data`, and a trigger has none of those filled in - so it rendered as
// "trigger", "active" and two em-dashes. These are the questions someone actually has about a
// trigger: what sets it off, when, who hears about it, and what it says.
//
// `recipients` are the names of its children, which is who the message fans out to. The node
// document does not carry them (children_ids are ids), so the caller resolves them from the graph.
//
// Empty facts are dropped rather than shown as a dash - except the two whose ABSENCE is the point:
// a trigger with no recipients does nothing at all, and one allowed to act unattended is worth
// saying out loud.
// The comparison inside `when`, as one phrase for a read-only card - '' when there is none.
//
// rulePreview's WORD labels ('equals', 'is at least') rather than describeCondition's arrow symbols:
// this is a sentence in a card, not a label on an arrow. The field keeps its ROOT ('after.status'),
// which is what tells two opposite gates apart on screen.
//
// A rule the form cannot render is NAMED rather than hidden. `add` drops empty values, so saying
// nothing would make a gated trigger indistinguishable from an ungated one.
// The whole "Only when" list in one phrase, both halves together and joined with "and" - which is
// what a list of narrowings means.
//
// BOTH halves, because until now only the comparison was shown: a trigger could carry a changed-field
// filter that nothing on any screen mentioned, so a trigger that never fired looked identical to one
// that fired on everything. That was tolerable while the filter was derived and invisible everywhere;
// it is not, now that it is something a person typed and may want to check.
function conditionFact(doc: Record<string, any>): string {
  const parts = storedChanged(doc).map(f => `${f} has changed`);
  const rule = storedIf(doc);
  if (rule) {
    const cmps = parseAllRule(rule);
    if (cmps === null) {
      parts.push('an advanced rule');
    } else {
      for (const c of cmps) { parts.push(rulePreview(c.field, c.operator, c.value)); }
    }
  }
  // FILTERED, because rulePreview returns '' for a comparison with no field - the `{var: ''}` gate
  // validation refuses but a document written straight into Firestore can still carry. Joining it
  // unfiltered produced "status has changed and " or a leading " and ", which reads as a fact that
  // was truncated rather than one describing a broken rule.
  return parts.filter(p => (p || '').trim()).join(' and ');
}

export function triggerFacts(
  node: { data?: Record<string, any> } | null | undefined,
  recipients: string[] = []
): TriggerFact[] {
  const data = node?.data || {};
  const facts: TriggerFact[] = [];
  const add = (label: string, value: any) => {
    const text = (value === null || value === undefined ? '' : String(value)).trim();
    if (text) { facts.push({ label, value: text }); }
  };

  add('Fires on', TRIGGER_KIND_LABELS[data['trigger_type']] || data['trigger_type']);

  // Only a scheduled trigger has a rhythm, and it is read back as the same sentence the editor
  // shows - never the cron string, which is not what anyone picked.
  if (data['trigger_type'] === 'cron') {
    const clock = group(data, 'cron');
    const spec = parseCron(clock['expression'] || '', clock['run_at']);
    // A hand-written expression has no sentence, so the expression itself is shown - better than
    // describing it as the nearest frequency, which would not be what runs.
    add('When', spec
      ? describeSchedule(spec, clock['timezone'] || DEFAULT_TIMEZONE)
      : (clock['expression'] || ''));
    // nextRunAt and scheduleError are deliberately NOT shown. They are the scheduler's own state -
    // a raw Timestamp and a message like "unknown IANA timezone" - which is engine detail rather
    // than something the reader of an org chart is asking about. Christian's call, 2026-08-06.
    //
    // Still WRITTEN THROUGH by triggerDataPatch (the cron group is spread, not rebuilt), so nothing
    // is lost by not rendering it - only hidden.
  }
  if (data['trigger_type'] === 'document') {
    const doc = group(data, 'document');
    add('Watches', doc['watch_collection']);
    add('On', Array.isArray(doc['on']) ? doc['on'].join(', ') : '');
    // The gate on top of the events. Without it a conditioned trigger reads exactly like an
    // unconditioned one, so a condition that had been destroyed was invisible.
    add('Only when', conditionFact(doc));
  }

  // Said even when empty: a trigger with no children fans out to nobody, so it is configured and
  // inert, which is invisible unless the card admits it.
  facts.push({
    label: 'Sends to',
    value: recipients.filter(Boolean).length ? recipients.filter(Boolean).join(', ') : 'nobody yet'
  });

  add('Says', data['payload']);

  const fields = payloadFieldNames(data['defaultData']);
  if (fields.length) { add('Brings', fields.join(', ')); }

  // Only when true, and phrased as what it MEANS rather than as a flag name - this is the setting
  // that lets an unattended run act without anyone approving it.
  if (data['allow_sensitive_actions'] === true) {
    facts.push({ label: 'Permissions', value: 'Acts without asking for confirmation' });
  }
  return facts;
}

// A trigger's read-only card in its fullest form: a plain-language SUMMARY of what it does, the
// FACTS (what fires it, when, its address and signing, who it sends to, its permissions), and the
// INSTRUCTION each recipient actually receives - which is the substance of the workflow and the one
// thing triggerFacts never showed. Used by the indented table's detail card; the compact canvas pane
// still uses triggerFacts.
//
// Pure and testable, like triggerFacts. Two things differ from it:
//   - `recipients` carry BOTH id and name: the id maps a recipient to its instruction in `messages`,
//     the name is what the reader sees.
//   - `webhookUrl` is passed IN rather than derived: the address depends on the receiver's host (an
//     app-level project id) this pure helper does not know. Empty when unregistered or non-webhook.
export interface TriggerRecipientLine {
  id: string;
  name: string;
  isAgent: boolean;
  // A real PERSON (not an agent, not a grouping node like a department/team). The only kind whose
  // message becomes a to-do; everything else shows its instruction plainly.
  isHuman: boolean;
  // Display label for the recipient's kind: 'AI agent' | 'Person' | 'Department' | 'Team' | ...
  // Read off the real kind so a department never falls through to "Person".
  role: string;
  // '' when this recipient has no instruction yet - rendered as "No instruction yet" rather than
  // hidden, so a half-configured workflow reads as half-configured instead of looking complete.
  message: string;
  // A pipeline STEP. It takes no instruction - the backend calls it with the previous step's output -
  // so its empty message is not something missing, and the card says so instead of "No instruction yet".
  isPipeline: boolean;
}

export interface TriggerCard {
  summary: string;
  facts: TriggerFact[];
  // The webhook address for a webhook trigger (null for any other kind). Its own row with a Copy
  // control rather than a plain fact - it is long and the whole point of showing it is to copy it.
  webhookUrl: string | null;
  // EVERY recipient, in order, each with its (possibly empty) instruction, kind and whether it is an
  // agent. Replaces the old "Sends to" fact - the list already names who it goes to.
  recipients: TriggerRecipientLine[];
}

// The recipient's kind, in words. NOT a binary agent/person: a department, team or division recipient
// must read as itself, not fall through to "Person".
function recipientRole(kind: string, isAgent: boolean): string {
  if (isAgent) { return 'AI agent'; }
  switch ((kind || '').toLowerCase()) {
    case 'person':
    case 'human': return 'Person';
    case 'department': return 'Department';
    case 'team': return 'Team';
    case 'division': return 'Division';
    default: return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : 'Recipient';
  }
}

export function triggerCard(
  node: { data?: Record<string, any> } | null | undefined,
  recipients: { id: string; name: string; isAgent?: boolean; kind?: string }[] = [],
  webhookUrl = ''
): TriggerCard {
  const data = node?.data || {};
  const kind = data['trigger_type'];

  const facts: TriggerFact[] = [];
  const add = (label: string, value: any) => {
    const text = (value === null || value === undefined ? '' : String(value)).trim();
    if (text) { facts.push({ label, value: text }); }
  };

  add('Fires on', TRIGGER_KIND_LABELS[kind] || kind);

  // The same sentence the editor shows for a schedule, never the cron string, which is not what
  // anyone picked. Kept in `when` so the summary can reuse it.
  let when = '';
  if (kind === 'cron') {
    const clock = group(data, 'cron');
    const spec = parseCron(clock['expression'] || '', clock['run_at']);
    when = spec ? describeSchedule(spec, clock['timezone'] || DEFAULT_TIMEZONE) : (clock['expression'] || '');
    add('When', when);
  }
  const doc = group(data, 'document');
  if (kind === 'document') {
    add('Watches', doc['watch_collection']);
    add('On', Array.isArray(doc['on']) ? doc['on'].join(', ') : '');
    add('Only when', conditionFact(doc));
  }
  if (kind === 'webhook') {
    const hook = group(data, 'webhook');
    // Signing defaults to ON, so a missing key reads as Required, matching triggerDraftFromNode.
    // The URL is carried on `webhookUrl` (its own copyable row), not as a fact.
    add('Signature', hook['require_signature'] !== false ? 'Required' : 'Not required');
    if (hook['require_idempotency_key'] === true) { add('Idempotency key', 'Required'); }
  }

  if (data['allow_sensitive_actions'] === true) {
    facts.push({ label: 'Permissions', value: 'Acts without asking for confirmation' });
  }

  // EVERY recipient, in order - not just the ones with an instruction. A blank one becomes a visible
  // "No instruction yet" row rather than vanishing, so a half-built workflow reads as half-built.
  // Messages are collapsed to friendly tokens (messageFor: the document holds {{after.vorname}}, this
  // shows {{first_name}}).
  const lines: TriggerRecipientLine[] = recipients.map(r => {
    const isAgent = r.isAgent === true;
    const rkind = (r.kind || 'person').toLowerCase();
    return {
      id: r.id, name: r.name, isAgent,
      isHuman: !isAgent && (rkind === 'person' || rkind === 'human'),
      role: recipientRole(rkind, isAgent),
      message: messageFor(data, r.id),
      isPipeline: rkind === 'pipeline'
    };
  });

  return {
    summary: triggerSummary(kind, when, doc, recipients.length > 0),
    facts,
    webhookUrl: kind === 'webhook' ? (webhookUrl || '') : null,
    recipients: lines
  };
}

// One plain sentence for the top of the card: what sets this trigger off, in words rather than field
// values. The recipients clause is only added when there are NONE, because that is the case whose
// consequence (nothing runs) is not obvious from the facts.
function triggerSummary(
  kind: string, when: string, doc: Record<string, any>, hasRecipients: boolean
): string {
  let base: string;
  switch (kind) {
    case 'webhook':
      base = "Starts a workflow when a request arrives at this trigger's webhook URL.";
      break;
    case 'cron':
      base = when
        ? `Runs on a schedule (${when}) and starts a workflow each time.`
        : 'Runs on a schedule and starts a workflow each time.';
      break;
    case 'document': {
      const collection = (doc['watch_collection'] || '').trim() || 'a collection';
      const events = Array.isArray(doc['on']) && doc['on'].length
        ? doc['on'].map(documentEventWord).join(' or ')
        : 'changed';
      base = `Starts a workflow when a document in ${collection} is ${events}.`;
      break;
    }
    default:
      base = 'Starts a workflow when it fires.';
  }
  return hasRecipients ? base : `${base} It has no recipients yet, so nothing runs.`;
}

// The stored change events ('create'/'update'/'delete') read as verbs; the summary wants the past
// tense ("is created or updated"). An unknown value is left as-is rather than dropped.
function documentEventWord(event: string): string {
  const map: Record<string, string> = { create: 'created', update: 'updated', delete: 'deleted' };
  return map[event] || event;
}

// True once the draft differs from what was loaded, i.e. there is something worth saving. Compared
// on the SAVED shape rather than field by field, so a change that makes no difference to the stored
// document (retyping the same text, or editing a schedule while the kind is not scheduled) does not
// light up the Save button.
export function isTriggerDraftDirty(draft: TriggerDraft, original: TriggerDraft): boolean {
  if ((draft?.name || '').trim() !== (original?.name || '').trim()) { return true; }
  return JSON.stringify(triggerDataPatch(draft)) !== JSON.stringify(triggerDataPatch(original));
}

// --- Webhook test call --------------------------------------------------------------------------

// The receiver: two cloud functions on the CUSTOMER'S OWN project. organigramCreateWebhook mints a
// registration, organigramWebhook is the address that registration publishes.
//
// Derived, not hardcoded. It was `europe-west3-mona-test1` - one project's host, which every other
// customer would have posted to - and before that `localhost:8080`, which only worked on the machine
// running the dev receiver. The project varies per customer exactly as it does for the organigram
// backend; the region does not (europe-west3), matching the other cloud functions this app calls
// (see company-knowledge.service.ts).
//
// Pure, so this module keeps its no-services rule: the caller supplies the id, which is why both of
// these take it rather than reading it.
const FUNCTIONS_REGION = 'europe-west3';

// '' when the project is not known yet - customerDB.db is not set until connectDB runs, and a host
// built from an empty id ('europe-west3-.cloudfunctions.net') is one nobody can read an error from.
// Callers treat the empty string as "not ready", the same shape processCollectionPath uses.
export function webhookReceiverBase(projectId: string): string {
  const id = (projectId || '').trim();
  return id ? `https://${FUNCTIONS_REGION}-${id}.cloudfunctions.net` : '';
}

// Where registration is requested. `send` POSTs here and reads the webhookId and secret back out.
export function webhookRegisterUrl(projectId: string): string {
  const base = webhookReceiverBase(projectId);
  return base ? `${base}/organigramCreateWebhook` : '';
}

// The body organigramCreateWebhook takes. THREE fields, and registering is all it does - it is not a test
// call and nothing is fired by it.
//
// It used to carry a `payload` too, on the assumption that registering and sending a sample request
// were one call. They are not: this registers, and a real request is learned by listening for one on
// the capture step. The extra key made the registration look like it published an instruction, which
// it never did.
//
// `organigramId` + `nodeId` are the pair that identifies the trigger: which company, and which node
// in it. The receiver needs both to write a received request back onto the right node, which is
// where the listener reads it from. `requireSignature` is what the receiver stores and checks
// incoming requests against - it exists here and nowhere else.
export function buildWebhookRegisterBody(
  organigramId: string, nodeId: string, requireSignature = true
): Record<string, any> {
  return {
    data: {
      organigramId: organigramId || '',
      nodeId: nodeId || '',
      requireSignature
    }
  };
}

// A trigger's workflow_id, which the backend contract requires on create.
//
// TEMPORARY and deliberately meaningless: it points at nothing. The design it came from - a separate
// workflowData collection holding the trigger's config, with the node keeping only a reference - was
// dropped, because that config is 1:1 with its trigger and belongs ON it (a reference is for data
// shared by many nodes, which is why locations are one and this is not). The field outlives the idea
// only because the contract still asks for it, and Christian is removing it.
//
// Random rather than derived from the node: a derived value would look like it MEANS something and
// invite code to parse it back. `wf_` matches the shape the backend's own examples use.
export function newWorkflowId(): string {
  const raw = typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function'
    ? (crypto as any).randomUUID().replace(/-/g, '')
    // Fallback for a non-secure context, where randomUUID is absent. Uniqueness here only has to
    // hold within one account's triggers, not globally.
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `wf_${raw.slice(0, 20)}`;
}

// The registration, as the keys a trigger node stores. Pure and shared so the wizard's write-back
// and any later re-registration cannot disagree about the field names.
//
// This is only ever HALF a `data` object. It must be merged over what the node already holds, never
// sent alone: a PATCH replaces `data` wholesale, so posting just these four deletes the identity the
// whole run is attributed to. That bug shipped once - the create wrote user_id / account_id /
// allow_sensitive_actions / workflow_id and the write-back a second later removed all four.
export function webhookData(reg: {
  webhookId?: string; url?: string; secret?: string; docId?: string;
} | null | undefined, existingGroup: Record<string, any> = {}): Record<string, any> {
  return {
    webhook: {
      ...existingGroup,
      webhook_id: reg?.webhookId || null,
      secret: reg?.secret || null
    }
  };
}

// The path segment the receiver mounts its webhooks on. The full address is this plus the id, which
// is why the backend keeps only `webhook_id` and drops `webhook_url` - the URL is DERIVED, and
// storing a derived value gives you two things that can disagree (the same reason cron is not stored
// beside schedule_spec).
export const WEBHOOK_URL_PATH = 'organigramWebhook';

// The address for a webhook id. Base passed in rather than read here, so this stays testable without
// the environment - callers use WEBHOOK_URL_BASE below.
export function webhookUrlFor(id: string, base: string): string {
  if (!id) { return ''; }
  return `${(base || '').replace(/\/+$/, '')}/${WEBHOOK_URL_PATH}/${id}`;
}

// The inverse of webhookData: the registration a trigger already has, or null when it has never been
// registered. Read rather than re-fetched, because editing must NOT mint a new webhook - the address
// is published, and re-registering would strand whoever is posting to the old one.
//
// A STORED url wins when there is one: the receiver is the authority on its own address, and a
// trigger registered against a different host must keep pointing there. Deriving is the fallback,
// which is the normal case now that the backend does not persist the url.
export function webhookFromData(
  data: Record<string, any> | null | undefined, base = ''
): { id: string; url: string; secret: string } | null {
  const hook = webhookGroup(data);
  // Nested under data.webhook, which is the only shape written. The flat spellings were read here as
  // a fallback for triggers that predate the grouping; there are none, and webhookData has only ever
  // written the group.
  const id = hook['webhook_id'] || '';
  const stored = hook['url'] || '';
  if (!id && !stored) { return null; }
  return { id, url: stored || webhookUrlFor(id, base), secret: hook['secret'] || '' };
}

// --- Listening for a received webhook -----------------------------------------------------------
//
// The test is INBOUND: you copy the URL, hit it from curl or the real sender, and this waits for the
// request to land. That is the object worth reading - the sender's actual shape - which is what
// {{data.email}} has to be written against. An outbound call would only echo back what you typed.
//
// The arrival is written onto the TRIGGER NODE, so listening is just re-reading the node. That is
// why there is no history collection here: polling goes through GET /nodes/{id} like everything
// else, which means no second store, no Firestore rule to grant, and no way to read a request
// belonging to another trigger - the node IS the scope.
//
// Polled rather than subscribed. The window is short and explicitly opened by the user, so a
// snapshot listener would buy latency we do not need in exchange for a connection to manage.
//
// THE FIELD NAMES BELOW ARE ASSUMPTIONS, collected here so they are one edit to correct - nothing
// has written one yet, so none has been seen against real data.
// Both live inside data.webhook now, alongside the toggles and the registration.
export const TRIGGER_ARRIVAL = {
  group: 'webhook',
  // When the last request landed. This is the CHANGE DETECTOR, not a date to compare against the
  // clock - see readSample.
  stampField: 'last_sample_at',
  // The request itself.
  sampleField: 'last_sample'
};

export interface TriggerArrival {
  receivedAt: string;
  // The request as it landed, whatever shape the sender used.
  body: any;
}

// The node's current sample and the stamp that marks it.
//
// The stamp is compared for INEQUALITY against a baseline taken when listening began, never against
// the clock. That matters: the stamp is written by the server and the clock would be the browser's,
// so any skew where the server runs behind would make a genuine arrival look older than the cutoff
// and be ignored for as long as the user waited. A value that simply differs from what was there
// before cannot be fooled by either clock.
//
// Normalised to a string so the comparison is a plain one whatever Firestore hands back - Timestamp,
// Date and ISO string all occur depending on how the field was written.
export function readSample(data: Record<string, any> | null | undefined): { stamp: string; sample: any } {
  const raw = webhookGroup(data);
  return { stamp: stampToString(raw[TRIGGER_ARRIVAL.stampField]), sample: raw[TRIGGER_ARRIVAL.sampleField] };
}

// An arrival, if the stamp has moved off the baseline. Null while it is unchanged - which is the
// ordinary answer on most polls and is not an error.
//
// An EMPTY stamp never counts as a change, so a node that has never received anything does not read
// as having just received nothing.
export function pickArrival(
  data: Record<string, any> | null | undefined, baseline: string
): TriggerArrival | null {
  const { stamp, sample } = readSample(data);
  if (!stamp || stamp === baseline) { return null; }
  return { receivedAt: stamp, body: sample };
}

function stampToString(value: any): string {
  if (value === null || value === undefined) { return ''; }
  if (typeof value === 'string') { return value; }
  if (typeof value === 'number') { return String(value); }
  if (typeof value.toDate === 'function') { return value.toDate().toISOString(); }
  if (value instanceof Date) { return value.toISOString(); }
  if (typeof value.seconds === 'number') { return `${value.seconds}.${value.nanoseconds || 0}`; }
  return JSON.stringify(value);
}

// How long to keep asking, and how often. Two minutes is long enough to switch to a terminal, paste
// a curl and come back; a 2s gap is under the threshold where a wait feels stuck.
export const LISTEN_INTERVAL_MS = 2000;
export const LISTEN_TIMEOUT_MS = 120000;

// How many arrivals to keep. Capped because listening no longer stops at the first one, and a panel
// that grows without limit while someone hammers the endpoint is its own problem. Ten is more than
// anyone compares by eye.
export const MAX_ARRIVALS = 10;

// Every dotted path in a received payload, so the user can read the template placeholders straight
// off what actually arrived instead of guessing what the sender calls its fields.
//
// Nested to the leaves, because `{{data.customer.email}}` is a real thing to want and a top-level
// list would stop exactly where the interesting part starts. Arrays report the path once rather than
// per index - `items[0].sku` and `items[1].sku` are the same field to whoever writes the template.
//
// The `[]` form IS resolvable by the message renderer (Christian, 2026-08-13). That matters more
// than it looks: 25 of the 36 applicant chips and 14 of the 24 dailyTodos chips are array paths, so
// if it were not, two thirds of both collections would render empty and read as a broken trigger
// rather than a bad placeholder. Do not switch this to `[0]` or drop element paths without checking
// that again.
export function payloadPathsOf(body: any, prefix = 'data', depth = 0): string[] {
  if (depth > 6 || body === null || body === undefined) { return []; }
  if (Array.isArray(body)) {
    return body.length ? payloadPathsOf(body[0], `${prefix}[]`, depth + 1) : [prefix];
  }
  if (typeof body !== 'object') { return [prefix]; }
  const keys = Object.keys(body);
  if (!keys.length) { return [prefix]; }
  const out: string[] = [];
  for (const key of keys) {
    for (const path of payloadPathsOf(body[key], `${prefix}.${key}`, depth + 1)) {
      if (out.indexOf(path) < 0) { out.push(path); }
    }
  }
  return out;
}
